import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  HStack,
  IconButton,
  Modal,
  Stack,
  Text,
  Tooltip,
} from "components/ui";
import {
  TranslationTabs,
  TranslationValues,
  pruneEmptyTranslations,
} from "components/common/TranslationTabs";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import {
  MdAccessTime,
  MdAdd,
  MdBarChart,
  MdBookmark,
  MdCalendarToday,
  MdChatBubbleOutline,
  MdClose,
  MdDelete,
  MdDescription,
  MdEdit,
  MdEmail,
  MdFlashOn,
  MdGroup,
  MdHelpOutline,
  MdLabel,
  MdNotifications,
  MdSearch,
  MdSettings,
  MdShoppingCart,
  MdStar,
} from "react-icons/md";
import { ShortcutCard } from "services/widgetService";

interface ShortcutCardEditorProps {
  cards: ShortcutCard[];
  onChange: (cards: ShortcutCard[]) => void;
}

interface ModalState {
  open: boolean;
  editingIndex: number | null;
  title: string;
  prompt: string;
  icon: string;
  /** Language code → { title?, prompt? }. Empty when the card is single-language. */
  translations: Record<string, TranslationValues>;
}

const MAX_CARDS = 8;
const DEFAULT_ICON = "zap";

const ICON_OPTIONS: {
  id: string;
  label: string;
  component: React.ElementType;
}[] = [
  { id: "zap", label: "Zap", component: MdFlashOn },
  { id: "bar-chart", label: "Chart", component: MdBarChart },
  { id: "help-circle", label: "Help", component: MdHelpOutline },
  { id: "message", label: "Chat", component: MdChatBubbleOutline },
  { id: "search", label: "Search", component: MdSearch },
  { id: "file-text", label: "Document", component: MdDescription },
  { id: "settings", label: "Settings", component: MdSettings },
  { id: "star", label: "Star", component: MdStar },
  { id: "calendar", label: "Calendar", component: MdCalendarToday },
  { id: "clock", label: "Clock", component: MdAccessTime },
  { id: "users", label: "Team", component: MdGroup },
  { id: "shopping-cart", label: "Cart", component: MdShoppingCart },
  { id: "mail", label: "Email", component: MdEmail },
  { id: "bell", label: "Alert", component: MdNotifications },
  { id: "bookmark", label: "Bookmark", component: MdBookmark },
  { id: "tag", label: "Tag", component: MdLabel },
];

function CardIcon({ iconId, size = 16 }: { iconId?: string; size?: number }) {
  const match = ICON_OPTIONS.find((o) => o.id === (iconId ?? DEFAULT_ICON));
  const Comp = (match ?? ICON_OPTIONS[0]).component;
  return <Comp size={size} />;
}

export const ShortcutCardEditor = ({
  cards,
  onChange,
}: ShortcutCardEditorProps) => {
  const { t } = useTranslation();

  const [modal, setModal] = useState<ModalState>({
    open: false,
    editingIndex: null,
    title: "",
    prompt: "",
    icon: DEFAULT_ICON,
    translations: {},
  });

  const openAdd = () =>
    setModal({
      open: true,
      editingIndex: null,
      title: "",
      prompt: "",
      icon: DEFAULT_ICON,
      translations: {},
    });

  const openEdit = (index: number) =>
    setModal({
      open: true,
      editingIndex: index,
      title: cards[index].title,
      prompt: cards[index].prompt,
      icon: cards[index].icon ?? DEFAULT_ICON,
      translations: cards[index].translations ?? {},
    });

  const closeModal = () =>
    setModal({
      open: false,
      editingIndex: null,
      title: "",
      prompt: "",
      icon: DEFAULT_ICON,
      translations: {},
    });

  const handleSave = () => {
    const trimmedTitle = modal.title.trim();
    const trimmedPrompt = modal.prompt.trim();
    if (!trimmedTitle || !trimmedPrompt) return;

    const translations = pruneEmptyTranslations(modal.translations);

    if (modal.editingIndex !== null) {
      onChange(
        cards.map((c, i) =>
          i === modal.editingIndex
            ? {
                ...c,
                title: trimmedTitle,
                prompt: trimmedPrompt,
                icon: modal.icon,
                translations,
              }
            : c,
        ),
      );
    } else {
      onChange([
        ...cards,
        {
          id: crypto.randomUUID(),
          title: trimmedTitle,
          prompt: trimmedPrompt,
          order: cards.length,
          icon: modal.icon,
          translations,
        },
      ]);
    }
    closeModal();
  };

  const handleDelete = (index: number) => {
    onChange(cards.filter((_, i) => i !== index));
  };

  const canAdd = cards.length < MAX_CARDS;
  const isSaveDisabled = !modal.title.trim() || !modal.prompt.trim();
  const isEditing = modal.editingIndex !== null;

  return (
    <>
      <Stack gap={4}>
        {cards.length === 0 ? (
          <Box
            p={4}
            borderRadius="lg"
            borderWidth="1px"
            borderStyle="dashed"
            borderColor="var(--border-default)"
            textAlign="center"
          >
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.widgets.ux.shortcuts.noCards", {
                defaultValue:
                  "No shortcut cards configured. Add one to display quick-action buttons on the welcome screen.",
              })}
            </Text>
          </Box>
        ) : (
          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={3}>
            {cards.map((card, index) => (
              <Card.Root key={card.id} variant="outline">
                <Card.Body p={3}>
                  <Flex justify="space-between" align="flex-start" gap={2}>
                    <HStack gap={2} flex={1} minW={0} align="flex-start">
                      <Box
                        flexShrink={0}
                        mt="2px"
                        color="var(--text-secondary-color)"
                      >
                        <CardIcon iconId={card.icon} size={15} />
                      </Box>
                      <Stack gap={1} minW={0}>
                        <Text
                          fontSize="sm"
                          fontWeight={600}
                          color="var(--text-primary)"
                          lineClamp={1}
                        >
                          {card.title}
                        </Text>
                        <Text
                          fontSize="xs"
                          color="var(--text-secondary-color)"
                          lineClamp={2}
                        >
                          {card.prompt}
                        </Text>
                      </Stack>
                    </HStack>
                    <HStack gap={1} flexShrink={0}>
                      <IconButton
                        aria-label={t(
                          "app.widgets.ux.shortcuts.editAriaLabel",
                          {
                            defaultValue: "Edit card",
                          },
                        )}
                        size="xs"
                        variant="ghost"
                        onClick={() => openEdit(index)}
                      >
                        <MdEdit />
                      </IconButton>
                      <IconButton
                        aria-label={t(
                          "app.widgets.ux.shortcuts.deleteAriaLabel",
                          { defaultValue: "Delete card" },
                        )}
                        size="xs"
                        variant="ghost"
                        color="red.500"
                        onClick={() => handleDelete(index)}
                      >
                        <MdDelete />
                      </IconButton>
                    </HStack>
                  </Flex>
                </Card.Body>
              </Card.Root>
            ))}
          </Grid>
        )}

        <Box>
          <Button
            intent="secondary"
            size="sm"
            onClick={openAdd}
            disabled={!canAdd}
          >
            <MdAdd />
            {t("app.widgets.ux.shortcuts.addCard", {
              defaultValue: "Add shortcut",
            })}
          </Button>
          {!canAdd && (
            <Text fontSize="xs" color="var(--text-secondary-color)" mt={1}>
              Maximum {MAX_CARDS} shortcuts allowed.
            </Text>
          )}
        </Box>
      </Stack>

      {/* lg, not md — the language tab strip plus a 3-row textarea is cramped at md. */}
      <Modal isOpen={modal.open} onClose={closeModal} size="lg">
        <Modal.Header>
          <Flex justify="space-between" align="center">
            <Text fontWeight={600} fontSize="md" color="var(--text-primary)">
              {isEditing
                ? t("app.widgets.ux.shortcuts.editCard", {
                    defaultValue: "Edit shortcut",
                  })
                : t("app.widgets.ux.shortcuts.addCard", {
                    defaultValue: "Add shortcut",
                  })}
            </Text>
            <Modal.CloseTrigger asChild>
              <IconButton
                aria-label="Close"
                variant="ghost"
                size="sm"
                onClick={closeModal}
              >
                <MdClose />
              </IconButton>
            </Modal.CloseTrigger>
          </Flex>
        </Modal.Header>

        <Modal.Body>
          <Stack gap={4}>
            <Stack gap={2}>
              <Text fontSize="sm" fontWeight={500} color="var(--text-primary)">
                {t("app.widgets.ux.shortcuts.iconLabel", {
                  defaultValue: "Icon",
                })}
              </Text>
              <Grid templateColumns="repeat(8, 1fr)" gap={1}>
                {ICON_OPTIONS.map((opt) => {
                  const isSelected = modal.icon === opt.id;
                  const Comp = opt.component;
                  return (
                    <Tooltip key={opt.id} content={opt.label}>
                      <Box
                        as="button"
                        onClick={() =>
                          setModal((prev) => ({ ...prev, icon: opt.id }))
                        }
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        w={8}
                        h={8}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={
                          isSelected
                            ? "var(--primary-color, #2563eb)"
                            : "var(--border-default)"
                        }
                        bg={
                          isSelected
                            ? "var(--primary-color-alpha, rgba(37,99,235,0.1))"
                            : "transparent"
                        }
                        color={
                          isSelected
                            ? "var(--primary-color, #2563eb)"
                            : "var(--text-secondary-color)"
                        }
                        cursor="pointer"
                        transition="all 0.15s"
                        aria-pressed={isSelected}
                        aria-label={opt.label}
                      >
                        <Comp size={16} />
                      </Box>
                    </Tooltip>
                  );
                })}
              </Grid>
            </Stack>

            {/* Icon sits above the tabs on purpose — it is language-neutral. */}
            <TranslationTabs
              fields={[
                {
                  key: "title",
                  label: t("app.widgets.ux.shortcuts.cardTitleLabel", {
                    defaultValue: "Title",
                  }),
                  type: "input",
                  required: true,
                  maxLength: 200,
                  placeholder: t(
                    "app.widgets.ux.shortcuts.cardTitlePlaceholder",
                    { defaultValue: "e.g. Get started" },
                  ),
                },
                {
                  key: "prompt",
                  label: t("app.widgets.ux.shortcuts.cardPromptLabel", {
                    defaultValue: "Prompt text",
                  }),
                  type: "textarea",
                  required: true,
                  maxLength: 2000,
                  rows: 3,
                  placeholder: t(
                    "app.widgets.ux.shortcuts.cardPromptPlaceholder",
                    { defaultValue: "e.g. What can you help me with?" },
                  ),
                },
              ]}
              defaultValues={{ title: modal.title, prompt: modal.prompt }}
              onDefaultChange={(values) =>
                setModal((prev) => ({
                  ...prev,
                  title: values.title ?? "",
                  prompt: values.prompt ?? "",
                }))
              }
              translations={modal.translations}
              onTranslationsChange={(next) =>
                setModal((prev) => ({ ...prev, translations: next }))
              }
            />
          </Stack>
        </Modal.Body>

        <Modal.Footer>
          <HStack justify="flex-end" gap={3}>
            <Button intent="secondary" onClick={closeModal}>
              {t("app.common.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              intent="primary"
              onClick={handleSave}
              disabled={isSaveDisabled}
            >
              {t("app.common.save", { defaultValue: "Save" })}
            </Button>
          </HStack>
        </Modal.Footer>
      </Modal>
    </>
  );
};
